import { Controller, Path, type FieldValues, type UseFormReturn, type ControllerRenderProps } from 'react-hook-form';
import { ReactElement, ReactNode } from 'react';
import { FormHelperText, Grid, InputLabel, Stack } from '@mui/material';
import { get } from 'lodash';

type RenderProps<Values extends FieldValues, Name extends Path<Values>> = ControllerRenderProps<Values, Name> & {
	readonly id: Name;
};

type Props<Values extends FieldValues, Name extends Path<Values>> = {
	readonly form: UseFormReturn<Values>;
	readonly name: Name;
	readonly label: string;
	readonly description?: string | ReactNode;
	readonly hint?: string;
	readonly render: (props: RenderProps<Values, Name>) => ReactElement;
};

const ControlledFormField = <Values extends FieldValues, Name extends Path<Values>>({
	form,
	name,
	label,
	description,
	hint,
	render,
}: Props<Values, Name>): ReactNode => {
	return (
		<Grid item xs={12}>
			<Stack spacing={1}>
				{label && (
					<InputLabel htmlFor={name} sx={{ fontWeight: 700 }}>
						{label}
					</InputLabel>
				)}
				{description && <FormHelperText>{description}</FormHelperText>}

				<Controller
					control={form.control}
					name={name}
					render={({ field }) =>
						render({
							...field,
							id: name,
						})
					}
				/>

				{get(form.formState.errors, name) == null ? (
					hint && <FormHelperText>{hint}</FormHelperText>
				) : (
					<FormHelperText error>{get(form.formState.errors, name)?.message as string}</FormHelperText>
				)}
			</Stack>
		</Grid>
	);
};
export { ControlledFormField };
